/**
 * 同期する端末状態(docs/04_data_model.md §5.1.1)。
 *
 * 「同じアカウントなら、どの端末・どのドメインでも全部そろう」ための土台。
 * 星・コイン・持ち物(`submit*` / `pullGacha` が書くもの)以外の端末状態は、
 * 下の表の section 単位で `saveUserState` へ送り、`readProgress` で読み戻す。
 *
 * | section    | localStorage キー                | 中身                                   |
 * |------------|----------------------------------|----------------------------------------|
 * | avatar     | `paltyping.inventory.v1`         | ベースキャラ・装備(`items` は送らない)|
 * | settings   | `paltyping.settings.v1`          | 難易度・音量・ことば・カリキュラム 等   |
 * | learning   | `paltyping.learning.v1`          | 打鍵統計                                |
 * | puzzle     | `paltyping.puzzleProgress.v1`    | 適応レベル・デモ既読                    |
 * | quizStats  | `paltyping.quizProgress.v1`      | にがて記録・デモ既読・こたえを みる回数 |
 * | activity   | `paltyping.activity.v1`          | 日×領域のセッション数                   |
 * | lessonSeen | `paltyping.learnLessonSeen.v1`   | 解説を見た単元                          |
 *
 * ここは **Firebase に触れない**(送信は `services/firebase/userStateSync.ts`)。
 * 役割は3つだけ:
 *
 *   1. **変更の検知** —— 各サービスが使う `KeyValueStorage` を包み、
 *      上の表のキーが書き換わったら「この section が変わった」と知らせる
 *      (各サービスの保存処理に1行も足さずに済む = 取りこぼしが構造的に起きない)
 *   2. **送る形の取り出し** —— 保存されている JSON から section ぶんを抜く
 *   3. **読み戻しの書き戻し** —— サーバーのマージ結果で localStorage を置き換える
 *      (**マージはサーバーの1か所だけ**。ここでは混ぜない)
 *
 * 保存形式・キー名は一切変えない(既存の子のデータをそのまま読み続ける)。
 */

import type { KeyValueStorage } from './progress';

/** 同期する section(サーバーの `USER_STATE_SECTIONS` と同じ並び)。 */
export const USER_STATE_SECTIONS = [
  'avatar',
  'settings',
  'learning',
  'puzzle',
  'quizStats',
  'activity',
  'lessonSeen',
] as const;

export type UserStateSection = (typeof USER_STATE_SECTIONS)[number];

/** section → localStorage キー(**既存のキー名は変えない**)。 */
export const USER_STATE_STORAGE_KEYS: Readonly<Record<UserStateSection, string>> = {
  avatar: 'paltyping.inventory.v1',
  settings: 'paltyping.settings.v1',
  learning: 'paltyping.learning.v1',
  puzzle: 'paltyping.puzzleProgress.v1',
  quizStats: 'paltyping.quizProgress.v1',
  activity: 'paltyping.activity.v1',
  lessonSeen: 'paltyping.learnLessonSeen.v1',
};

/** 未送信の印(オフライン・失敗のときに残す)。 */
export const USER_STATE_DIRTY_KEY = 'paltyping.userStateDirty.v1';

/** `avatar` として送るキー(`items` は送らない。持ち物はサーバーが正)。 */
const AVATAR_FIELDS = ['base', 'equipped'] as const;

/** `quizStats` として送るキー(`units` / `stages` は progress が正)。 */
const QUIZ_STATS_FIELDS = ['questions', 'demoSeen', 'revealed'] as const;

/** 設定のうち**送らない**キー(なまえ・生年月はサーバーの profile が正)。 */
const SETTINGS_DROPPED_FIELDS = ['displayName', 'birthYearMonth'] as const;

const SECTION_BY_KEY = new Map<string, UserStateSection>(
  USER_STATE_SECTIONS.map((section) => [USER_STATE_STORAGE_KEYS[section], section]),
);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// ── 変更の検知 ───────────────────────────────────────────────────────

type SectionListener = (section: UserStateSection) => void;

/** v2 の差分操作を組み立てるための、保存前後の通知。 */
export interface UserStateMutation {
  readonly section: UserStateSection;
  readonly before: string | null;
  readonly after: string | null;
  readonly occurredAt: number;
}

/** 学習記録など、保存元が差分を正確に知っている場合の操作。 */
export interface UserStateOperationDraft {
  readonly section: UserStateSection;
  readonly occurredAt: number;
  readonly payload: Record<string, unknown>;
  /** 同じ学習セッションを別端末から再送しても重複させない安定ID。 */
  readonly id?: string;
}

const listeners = new Set<SectionListener>();
const mutationListeners = new Set<(mutation: UserStateMutation) => void>();
const operationListeners = new Set<(operation: UserStateOperationDraft) => void>();
/** 読み戻しの書き戻し中は通知しない(自分の書込で送信ループを作らない)。 */
let quiet = 0;

/** 端末状態が書き換わったら呼ばれる。戻り値で購読を解除する。 */
export function onUserStateChanged(listener: SectionListener): () => void {
  listeners.add(listener);
  return () => void listeners.delete(listener);
}

export function onUserStateMutation(listener: (mutation: UserStateMutation) => void): () => void {
  mutationListeners.add(listener);
  return () => void mutationListeners.delete(listener);
}

export function onUserStateOperation(
  listener: (operation: UserStateOperationDraft) => void,
): () => void {
  operationListeners.add(listener);
  return () => void operationListeners.delete(listener);
}

/** 差分を知るサービスからの操作登録。読み戻し中は送信しない。 */
export function recordUserStateOperation(operation: UserStateOperationDraft): void {
  if (quiet > 0) return;
  for (const listener of [...operationListeners]) listener(operation);
}

/** テスト用。購読を全部外す。 */
export function resetUserStateListeners(): void {
  listeners.clear();
  mutationListeners.clear();
  operationListeners.clear();
  quiet = 0;
}

function notifyChanged(section: UserStateSection): void {
  if (quiet > 0) return;
  for (const listener of [...listeners]) {
    try {
      listener(section);
    } catch (error) {
      // 1つの購読者の都合で保存全体を止めない。
      console.warn('[paltyping] 端末状態の変更通知で例外が出ました', error);
    }
  }
}

function notifyMutation(mutation: UserStateMutation): void {
  if (quiet > 0) return;
  for (const listener of [...mutationListeners]) {
    try {
      listener(mutation);
    } catch (error) {
      console.warn('[paltyping] 端末状態の差分通知で例外が出ました', error);
    }
  }
}

/** 通知を出さずに書き換える(読み戻しの反映で使う)。 */
export function withoutUserStateNotify<T>(fn: () => T): T {
  quiet += 1;
  try {
    return fn();
  } finally {
    quiet -= 1;
  }
}

/**
 * 非同期のあいだ通知を止める(読み戻しの反映ぜんぶを1つのまとまりにする)。
 *
 * 読み戻しは持ち物・星を書いてから端末状態を重ねる。その途中の書込は
 * **サーバーから受け取ったものそのもの**なので、送り返す必要が無い
 * (止めないと、ログインのたびに無駄な `saveUserState` が1回増える)。
 * 待つのはマイクロタスクだけなので、そのあいだに子どもの操作は割り込まない。
 */
export async function withoutUserStateNotifyAsync<T>(fn: () => Promise<T>): Promise<T> {
  quiet += 1;
  try {
    return await fn();
  } finally {
    quiet -= 1;
  }
}

function safeLocalStorage(): KeyValueStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/**
 * `KeyValueStorage` を包んで、同期対象キーの書き換えを知らせる。
 *
 * **記録するのは section 名だけ**(ここで JSON.stringify はしない)。
 * 送るのは3秒デバウンスのあとにまとめて、が原則(CLAUDE.md ルール6)。
 */
export function observeStorage(storage: KeyValueStorage | null): KeyValueStorage | null {
  if (!storage) return null;
  return {
    getItem: (key) => storage.getItem(key),
    setItem: (key, value) => {
      const before = storage.getItem(key);
      storage.setItem(key, value);
      const section = SECTION_BY_KEY.get(key);
      if (section !== undefined) {
        notifyChanged(section);
        notifyMutation({ section, before, after: value, occurredAt: Date.now() });
      }
    },
    removeItem: (key) => {
      const before = storage.getItem(key);
      storage.removeItem(key);
      const section = SECTION_BY_KEY.get(key);
      if (section !== undefined) {
        notifyChanged(section);
        notifyMutation({ section, before, after: null, occurredAt: Date.now() });
      }
    },
  };
}

let observed: KeyValueStorage | null | undefined;

/**
 * アプリ全体で使う「変更を見張る localStorage」。
 *
 * 各ローカルサービスの既定の保存先をこれに差し替えるだけで、
 * どの保存経路からの書込でも同期の対象になる。
 */
export function appStorage(): KeyValueStorage | null {
  observed ??= observeStorage(safeLocalStorage());
  return observed;
}

// ── メモリキャッシュの読み直し ───────────────────────────────────────

const reloaders = new Set<() => void>();

/**
 * 「localStorage を読み直す」入口を登録する(モジュール単位のシングルトン用)。
 * 読み戻しのあとに `reloadUserStateCaches()` から一斉に呼ばれる。
 */
export function registerUserStateReload(reload: () => void): () => void {
  reloaders.add(reload);
  return () => void reloaders.delete(reload);
}

/** 登録済みのキャッシュを全部読み直させる。 */
export function reloadUserStateCaches(): void {
  for (const reload of [...reloaders]) {
    try {
      reload();
    } catch (error) {
      console.warn('[paltyping] 端末状態の読み直しで例外が出ました', error);
    }
  }
}

// ── 送る形の取り出し ─────────────────────────────────────────────────

function readFile(
  section: UserStateSection,
  storage: KeyValueStorage | null,
): Record<string, unknown> | null {
  const raw = storage?.getItem(USER_STATE_STORAGE_KEYS[section]) ?? null;
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isPlainObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function pick(source: Record<string, unknown>, fields: readonly string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const field of fields) {
    if (source[field] !== undefined) out[field] = source[field];
  }
  return out;
}

/**
 * その section をいま送るなら、という中身。保存が無ければ `null`。
 *
 * サーバーが無視する項目(`items` / `units` / `stages` / なまえ)は
 * **そもそも送らない**(通信量とプライバシーの両方で無駄)。
 */
export function readUserStateSection(
  section: UserStateSection,
  storage: KeyValueStorage | null = appStorage(),
): Record<string, unknown> | null {
  const file = readFile(section, storage);
  if (file === null) return null;
  switch (section) {
    case 'avatar':
      return pick(file, AVATAR_FIELDS);
    case 'quizStats':
      return pick(file, QUIZ_STATS_FIELDS);
    case 'settings': {
      const out = { ...file };
      for (const field of SETTINGS_DROPPED_FIELDS) delete out[field];
      return out;
    }
    default:
      return file;
  }
}

// ── 読み戻しの書き戻し ───────────────────────────────────────────────

function writeFile(
  section: UserStateSection,
  file: Record<string, unknown>,
  storage: KeyValueStorage | null,
): void {
  try {
    storage?.setItem(USER_STATE_STORAGE_KEYS[section], JSON.stringify(file));
  } catch {
    // 容量超過・プライベートモード等。次の読み戻しでまた入るだけ。
  }
}

/**
 * サーバーのマージ結果1件を localStorage へ**置き換え**で書き戻す。
 *
 * `avatar` と `quizStats` はファイルの一部しか持たない section なので、
 * サーバーが持たない項目(持ち物・単元の星)を残したまま重ねる。
 * 通知は出さない = 書き戻しがそのまま次の送信を呼ばない。
 */
export function writeUserStateSection(
  section: UserStateSection,
  data: unknown,
  storage: KeyValueStorage | null = appStorage(),
): void {
  if (!isPlainObject(data)) return;
  withoutUserStateNotify(() => {
    const file = readFile(section, storage) ?? {};
    switch (section) {
      case 'avatar':
        writeFile(section, { version: 1, ...file, ...pick(data, AVATAR_FIELDS) }, storage);
        return;
      case 'quizStats':
        writeFile(section, { version: 1, ...file, ...pick(data, QUIZ_STATS_FIELDS) }, storage);
        return;
      case 'settings':
        writeFile(section, { ...file, ...data }, storage);
        return;
      default:
        writeFile(section, data, storage);
    }
  });
}

/**
 * 読み戻した `state` を全 section ぶん書き戻す。
 *
 * `null` は「この UID にはまだその状態が無い」という正しい応答なので、前の
 * アカウントの localStorage を残してはいけない。`undefined` は古い Functions の
 * 互換応答として扱い、値を保持する。
 */
export function applyUserState(
  state: Partial<Record<UserStateSection, { readonly data?: unknown } | null>> | undefined,
  storage: KeyValueStorage | null = appStorage(),
): void {
  if (!state) return;
  for (const section of USER_STATE_SECTIONS) {
    const entry = state[section];
    if (entry === undefined) continue;
    if (entry === null) {
      withoutUserStateNotify(() => storage?.removeItem(USER_STATE_STORAGE_KEYS[section]));
      continue;
    }
    writeUserStateSection(section, entry.data, storage);
  }
}

/**
 * プロフィールは state/settings ではなくサーバーの profile が正。
 * 表示設定のキャッシュだけへ反映し、監視通知は出さない。
 */
export function applyProfileToSettings(
  profile: { readonly displayName?: unknown; readonly birthYearMonth?: unknown } | null | undefined,
  storage: KeyValueStorage | null = appStorage(),
): void {
  // `undefined` は古い Functions が profile を返さない互換応答なので保持する。
  // `null` はこのUIDにプロフィールが無いという正しい読み戻しなので、前の子の
  // 表示名・生年月を残さないよう空に戻す。
  if (profile === undefined) return;
  withoutUserStateNotify(() => {
    const current = readFile('settings', storage) ?? {};
    const next = { ...current };
    next.displayName = typeof profile?.displayName === 'string' ? profile.displayName : null;
    next.birthYearMonth =
      typeof profile?.birthYearMonth === 'string' ? profile.birthYearMonth : null;
    writeFile('settings', next, storage);
  });
}

// ── 未送信の印 ───────────────────────────────────────────────────────

/** 送りそこねた section(オフライン・失敗)。 */
export function readDirtySections(
  storage: KeyValueStorage | null = appStorage(),
): Set<UserStateSection> {
  const out = new Set<UserStateSection>();
  const raw = storage?.getItem(USER_STATE_DIRTY_KEY) ?? null;
  if (raw === null) return out;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return out;
    for (const value of parsed) {
      if (typeof value === 'string' && (USER_STATE_SECTIONS as readonly string[]).includes(value)) {
        out.add(value as UserStateSection);
      }
    }
  } catch {
    // 壊れていたら「未送信は無い」に落とす(次の変更でまた印が付く)。
  }
  return out;
}

export function writeDirtySections(
  sections: ReadonlySet<UserStateSection>,
  storage: KeyValueStorage | null = appStorage(),
): void {
  try {
    if (sections.size === 0) {
      storage?.removeItem(USER_STATE_DIRTY_KEY);
      return;
    }
    storage?.setItem(USER_STATE_DIRTY_KEY, JSON.stringify([...sections]));
  } catch {
    // 保存できなくてもメモリ上の印は残る(その起動のあいだは送り直せる)。
  }
}
